// Typed keys and paths for every file under packages/client/public/assets.
// Task 15's generation steps 1-7 are complete (see
// docs/superpowers/plans/2026-09-03-nanocars-v1.md, "Asset status"); Task 13
// is the one that actually loads these through pixi.js's Assets loader. This
// module is just the typed index so callers never hand-type a path string.

const BASE = '/assets';

// -- audio ------------------------------------------------------------------

export const AUDIO_KEYS = [
  'beep',
  'bump_1',
  'bump_2',
  'bump_3',
  'engine_loop',
  'fall',
  'finish',
  'go',
  'music_loop',
  'skid_1',
  'skid_2',
  'skid_3',
] as const;
export type AudioKey = (typeof AUDIO_KEYS)[number];

export const AUDIO_PATHS: Readonly<Record<AudioKey, string>> = Object.fromEntries(
  AUDIO_KEYS.map((k) => [k, `${BASE}/audio/${k}.mp3`]),
) as Record<AudioKey, string>;

// -- cars ---------------------------------------------------------------

export const CAR_BASE_PATH = `${BASE}/car_base.png`;

// Six liveries, one per player slot (see ui/theme.ts PLAYER_COLORS, same
// order). Each sheet is an 8x4 grid of 144 px frames; frame i faces heading
// i * 11.25 degrees clockwise from +x (32 directions total).
export const LIVERY_SLOTS = [0, 1, 2, 3, 4, 5] as const;
export type LiverySlot = (typeof LIVERY_SLOTS)[number];

export const LIVERY_PATHS: Readonly<Record<LiverySlot, string>> = Object.fromEntries(
  LIVERY_SLOTS.map((i) => [i, `${BASE}/cars/livery_${i}.png`]),
) as Record<LiverySlot, string>;

export const CAR_SHEET_PATHS: Readonly<Record<LiverySlot, string>> = Object.fromEntries(
  LIVERY_SLOTS.map((i) => [i, `${BASE}/cars/sheet_${i}.png`]),
) as Record<LiverySlot, string>;

export const CAR_SHEET_JSON_PATH = `${BASE}/cars/sheet.json`;
export const CAR_SHEET_FRAME_COUNT = 32;
export const CAR_SHEET_DEGREES_PER_FRAME = 360 / CAR_SHEET_FRAME_COUNT;

// -- props (kitchen-table obstacles/decorations) -----------------------------

export const PROP_KEYS = [
  'banana',
  'butter_dish',
  'cereal_box',
  'crumbs',
  'egg_cup',
  'fork',
  'jam_jar',
  'juice_carton',
  'knife',
  'milk_spill',
  'mug',
  'napkin',
  'pepper',
  'plate',
  'salt',
  'spoon',
  'sugar_cubes',
  'toast',
] as const;
export type PropKey = (typeof PROP_KEYS)[number];

export const PROP_PATHS: Readonly<Record<PropKey, string>> = Object.fromEntries(
  PROP_KEYS.map((k) => [k, `${BASE}/props/${k}.png`]),
) as Record<PropKey, string>;

// -- effects ------------------------------------------------------------

export const EFFECT_DUST_FRAMES = [0, 1, 2] as const;
export type EffectDustFrame = (typeof EFFECT_DUST_FRAMES)[number];

export const EFFECT_DUST_PATHS: Readonly<Record<EffectDustFrame, string>> = Object.fromEntries(
  EFFECT_DUST_FRAMES.map((i) => [i, `${BASE}/effects/dust_${i}.png`]),
) as Record<EffectDustFrame, string>;

export const EFFECT_SPLASH_PATH = `${BASE}/effects/splash.png`;

// -- surfaces (tileable table backgrounds) -----------------------------------

export const SURFACE_KEYS = ['table_wood', 'tablecloth'] as const;
export type SurfaceKey = (typeof SURFACE_KEYS)[number];

export const SURFACE_PATHS: Readonly<Record<SurfaceKey, string>> = Object.fromEntries(
  SURFACE_KEYS.map((k) => [k, `${BASE}/surfaces/${k}.jpg`]),
) as Record<SurfaceKey, string>;

// -- ui -------------------------------------------------------------------

export const UI_KEYS = ['button', 'card', 'icon_keys', 'icon_pad', 'logo'] as const;
export type UiKey = (typeof UI_KEYS)[number];

export const UI_PATHS: Readonly<Record<UiKey, string>> = Object.fromEntries(
  UI_KEYS.map((k) => [k, `${BASE}/ui/${k}.png`]),
) as Record<UiKey, string>;

// -- everything, flattened, for bulk preloading ------------------------------

export const ALL_ASSET_PATHS: readonly string[] = [
  ...Object.values(AUDIO_PATHS),
  CAR_BASE_PATH,
  ...Object.values(LIVERY_PATHS),
  ...Object.values(CAR_SHEET_PATHS),
  CAR_SHEET_JSON_PATH,
  ...Object.values(PROP_PATHS),
  ...Object.values(EFFECT_DUST_PATHS),
  EFFECT_SPLASH_PATH,
  ...Object.values(SURFACE_PATHS),
  ...Object.values(UI_PATHS),
];
